import {
  type SqlInstruction,
  awsRdsVpcIdOptionProvider,
  dbAwsRdsIamWriteCommands,
  gcpCloudSqlVpcIdOptionProvider,
} from "@p0security/integrations-db-frontend";
import { installTf } from "@p0security/integrations-postgres-shared/iac";
import {
  RDS_CREATE_USER,
  RDS_GRANT_IAM,
  RDS_GRANT_SUPERUSER,
} from "@p0security/integrations-postgres-shared/sql";
import type { PostgresIamWriteConfig } from "@p0security/integrations-postgres-shared/types";
import { Typography } from "@p0security/ui";

import type { PostgresInstaller } from "./types.ts";

/** Lists each role that owns a table in the schema, and which tables it owns. */
const IDENTIFY_TABLE_OWNERS_SQL = `SELECT tableowner, array_agg(tablename) AS tables
FROM pg_tables
WHERE schemaname = 'public'
GROUP BY tableowner;`;

const sqlTextAccessInstructions = (grantee: string): SqlInstruction[] => [
  {
    command: IDENTIFY_TABLE_OWNERS_SQL,
    header: (
      <div>
        <Typography.Paragraph>
          <b>Optional:</b> the following steps are only required if you need
          requestors to be able to request access for executing specific SQL
          statements. Set up either, both, or neither of the sections below,
          depending on what kinds of statements your requestors need to run. If
          you choose to skip these steps and SQL statement access is requested,
          P0 will be unable to provision access for that request.
        </Typography.Paragraph>
        <Typography.Paragraph>
          <b>1. If requestors need to perform actions on specific tables:</b>{" "}
          First identify the user that owns the tables you want P0 to be able to
          grant access to:
        </Typography.Paragraph>
      </div>
    ),
  },
  {
    command: `GRANT SELECT, INSERT, UPDATE, DELETE ON ALL TABLES IN SCHEMA public TO ${grantee} WITH GRANT OPTION;
ALTER DEFAULT PRIVILEGES IN SCHEMA public
  GRANT SELECT, INSERT, UPDATE, DELETE ON TABLES TO ${grantee} WITH GRANT OPTION;`,
    header: (
      <Typography.Paragraph>
        Then, for each role that owns a table, switch to that role and grant{" "}
        <Typography.Text code>{grantee}</Typography.Text> grant authority on its
        tables. The second statement covers tables that role creates in the
        future:
      </Typography.Paragraph>
    ),
  },
  {
    command: `GRANT USAGE, CREATE ON SCHEMA public TO ${grantee} WITH GRANT OPTION;`,
    header: (
      <div>
        <Typography.Paragraph>
          <b>
            2. If requestors need to perform schema-level actions on existing
            schemas:
          </b>
        </Typography.Paragraph>
        <ul>
          <li>
            Then, for each role that owns a schema, switch to that role and run
            this once per schema P0 should manage access to.
          </li>
          <li>
            This grants <Typography.Text code>{grantee}</Typography.Text>{" "}
            <Typography.Text code>USAGE</Typography.Text> (to reference the
            schema&apos;s tables) and{" "}
            <Typography.Text code>CREATE</Typography.Text> (to create new tables
            in it) on the schema, with grant option.
          </li>
          <li>
            If there are new schemas created in the future, this step will need
            to be run again.
          </li>
        </ul>
      </div>
    ),
  },
];

type PostgresIamWriteInstructions = NonNullable<
  PostgresInstaller["components"]["iam-write"]["instructions"]
>;

type PostgresHostedIamWriteInstructions<
  Hosting extends PostgresIamWriteConfig["hosting"],
> = (
  context: Parameters<PostgresIamWriteInstructions>[0],
  id: Parameters<PostgresIamWriteInstructions>[1],
  item: Parameters<PostgresIamWriteInstructions>[2] & {
    hosting: Hosting;
  }
) => ReturnType<PostgresIamWriteInstructions>;

const gcpCloudSqlIamWriteInstructions: PostgresHostedIamWriteInstructions<
  Extract<PostgresIamWriteConfig["hosting"], { type: "gcp-cloudsql" }>
> = (_context, _id, item) => {
  const dbUser =
    item.hosting.connectorServiceAccount?.replace(
      /\.gserviceaccount\.com$/,
      ""
    ) ?? "";
  const assignRolesCommand = `gcloud sql users assign-roles "${dbUser}" --project="${item.hosting.projectId}" --instance="${item.hosting.instanceName}" --type="CLOUD_IAM_SERVICE_ACCOUNT" --database-roles="cloudsqlsuperuser"`;
  return {
    help: (
      <>
        Follow the below instructions in order to install P0 on this CloudSQL
        instance, then click &quot;Next&quot;:
      </>
    ),
    commands: {
      shell: [
        {
          header: (
            <Typography.Paragraph>
              Run the following <b>gcloud</b> command to provision the P0
              connector user:
            </Typography.Paragraph>
          ),
          command: `gcloud sql users create "${dbUser}" --project="${item.hosting.projectId}" --instance="${item.hosting.instanceName}" --type="CLOUD_IAM_SERVICE_ACCOUNT"`,
        },
        {
          header: (
            <Typography.Paragraph>
              Then assign the user the{" "}
              <Typography.Text code>cloudsqlsuperuser</Typography.Text> role:
            </Typography.Paragraph>
          ),
          command: assignRolesCommand,
        },
        ...sqlTextAccessInstructions("cloudsqlsuperuser"),
      ],
      iac: [
        {
          // Note: for Postgres, the name parameter for this block should be the service account
          // email with the .gserviceaccount.com suffix trimmed out:
          // https://docs.cloud.google.com/sql/docs/postgres/add-manage-iam-users#terraform_2
          // Per the GCP docs, this suffix trimming is only required for postgres
          command: `
resource "google_sql_user" "iam_service_account" {
  project  = "${item.hosting.projectId}"
  instance = "${item.hosting.instanceName}"
  type     = "CLOUD_IAM_SERVICE_ACCOUNT"
  name     = "${dbUser}"
}
          `,
          header: (
            <div>
              <Typography.Paragraph>
                Apply the following terraform in order to provision the P0
                connector user.
              </Typography.Paragraph>
            </div>
          ),
        },
        {
          header: (
            <Typography.Paragraph>
              If you are using version 7.19.0 of the Google terraform provider
              or later, you can add the following line to the SQL user resource
              block (above).
            </Typography.Paragraph>
          ),
          command: `database_roles = ["cloudsqlsuperuser"]`,
        },
        {
          header: (
            <Typography.Paragraph>
              Otherwise, run the following <b>gcloud</b> command to assign the
              role:
            </Typography.Paragraph>
          ),
          command: assignRolesCommand,
        },
        ...sqlTextAccessInstructions("cloudsqlsuperuser"),
      ],
    },
  };
};

const awsRdsIamWriteInstructions: PostgresHostedIamWriteInstructions<
  Extract<PostgresIamWriteConfig["hosting"], { type: "aws-rds" }>
> = (context, id, item) => {
  return {
    help: (
      <>
        To install P0 on this account, provision P0&apos;s access using
        Terraform and granting access inside the database. You will need to run
        the SQL commands as an administrative user.
      </>
    ),
    commands: dbAwsRdsIamWriteCommands(
      context,
      id,
      item
    )({
      sqlInstructions: [
        {
          command: RDS_CREATE_USER,
          header: (
            <div>
              <Typography.Paragraph>
                First, configure the P0 connector&apos;s Postgres user.
              </Typography.Paragraph>
              <Typography.Paragraph>
                Create the{" "}
                <Typography.Text code>p0_iam_manager</Typography.Text> Postgres
                user. Run this as a role with the{" "}
                <Typography.Text code>CREATEROLE</Typography.Text> privilege.
              </Typography.Paragraph>
            </div>
          ),
        },
        {
          command: RDS_GRANT_IAM,
          header: (
            <Typography.Paragraph>
              Grant <Typography.Text code>rds_iam</Typography.Text> so the user
              can authenticate via IAM. Run this with any role that can create
              users and grant privileges.
            </Typography.Paragraph>
          ),
        },
        {
          command: RDS_GRANT_SUPERUSER,
          header: (
            <Typography.Paragraph>
              Grant <Typography.Text code>rds_superuser</Typography.Text> so the
              user can manage role memberships for IAM-authenticated access. Run
              this with the master user, or another role that holds{" "}
              <Typography.Text code>rds_superuser</Typography.Text> membership.
            </Typography.Paragraph>
          ),
        },
      ],
      postTfSqlInstructions: sqlTextAccessInstructions("p0_iam_manager"),
      installTf,
    }),
  };
};

export const postgresInstaller: PostgresInstaller = {
  components: {
    "iam-write": {
      items: {
        hosting: {
          options: {
            "aws-rds": {
              items: {
                vpcId: {
                  optionProvider: awsRdsVpcIdOptionProvider,
                },
              },
            },
            "gcp-cloudsql": {
              items: {
                vpcId: {
                  optionProvider: gcpCloudSqlVpcIdOptionProvider,
                },
              },
            },
          },
        },
      },
      instructions: (context, id, item) => {
        const { hosting } = item;
        return hosting.type === "gcp-cloudsql"
          ? gcpCloudSqlIamWriteInstructions(context, id, { ...item, hosting })
          : awsRdsIamWriteInstructions(context, id, { ...item, hosting });
      },
    },
  },
};
